import type { UserRole } from '../utils/constants';
import { supabase } from './supabaseClient';

// Types pour les entités de base de données
export interface User {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  role: UserRole;
  auth_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface Client {
  id: string;
  full_name: string;
  email?: string | null;
  phone?: string | null;
  phone2?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  vat_number?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface Article {
  id: string;
  code: string;
  reference?: string;
  name: string;
  description?: string;
  price: number;
  stock: number;
  active: boolean;
  created_at: string;
}

export interface Order {
  id: string;
  client_id: string;
  user_id: string;
  total_amount: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  created_at: string;
  updated_at: string;
}

// Service API pour les utilisateurs
export const userService = {
  // Récupérer le profil utilisateur par auth_user_id
  async getUserProfile(authUserId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase.rpc('get_user_profile_by_auth', {
        p_auth_user_id: authUserId,
      });

      if (error) {
        console.error('Erreur lors de la récupération du profil:', error);
        return null;
      }

      return data?.[0] || null;
    } catch (error) {
      console.error('Erreur API getUserProfile:', error);
      return null;
    }
  },

  // Mettre à jour le profil utilisateur
  async updateUserProfile(userId: string, updates: Partial<User>): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la mise à jour du profil:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur API updateUserProfile:', error);
      return null;
    }
  },

  // Lister tous les utilisateurs (admin seulement)
  async getAllUsers(): Promise<User[]> {
    try {
      const { data, error } = await supabase
        .from('users_with_roles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erreur API getAllUsers:', error);
      return [];
    }
  },
};

// Service API pour les clients
export const clientService = {
  // Récupérer tous les clients (fonction existante - gardée pour compatibilité)
  async getAllClients(): Promise<Client[]> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('full_name', { ascending: true });

      if (error) {
        console.error('Erreur lors de la récupération des clients:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erreur API getAllClients:', error);
      return [];
    }
  },

  // Nouvelle fonction : Récupérer les clients avec pagination côté serveur
  async getClientsPaginated(
    page: number = 1,
    limit: number = 10,
    searchTerm: string = ''
  ): Promise<{ clients: Client[]; totalCount: number }> {
    try {
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase
        .from('clients')
        .select('*', { count: 'exact' })
        .order('full_name', { ascending: true })
        .range(from, to);

      // Ajouter la recherche si un terme est fourni
      if (searchTerm.trim()) {
        query = query.or(
          `full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`
        );
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Erreur lors de la récupération paginée des clients:', error);
        return { clients: [], totalCount: 0 };
      }

      return {
        clients: data || [],
        totalCount: count || 0,
      };
    } catch (error) {
      console.error('Erreur API getClientsPaginated:', error);
      return { clients: [], totalCount: 0 };
    }
  },

  // 🔥 NOUVELLE MÉTHODE EFFICACE : Récupérer UN client par ID
  async getClientById(id: string): Promise<Client | null> {
    try {
      const { data, error } = await supabase.from('clients').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Client non trouvé
        console.error('Erreur lors de la récupération du client:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur API getClientById:', error);
      return null;
    }
  },

  // Récupérer le client par défaut (marqué is_default = true)
  async getDefaultClient(): Promise<Client | null> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('is_default', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        console.error('Client par défaut non trouvé:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur API getDefaultClient:', error);
      return null;
    }
  },

  // 🔥 NOUVELLE MÉTHODE EFFICACE : Compter UNIQUEMENT les clients
  async getClientsStats(): Promise<{ total: number; newThisMonth: number }> {
    try {
      // Compter le total
      const { count: total, error: totalError } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true });

      if (totalError) {
        console.error('Erreur comptage total clients:', totalError);
        return { total: 0, newThisMonth: 0 };
      }

      // Compter les nouveaux ce mois
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: newThisMonth, error: monthError } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString());

      if (monthError) {
        console.error('Erreur comptage nouveaux clients:', monthError);
        return { total: total || 0, newThisMonth: 0 };
      }

      return {
        total: total || 0,
        newThisMonth: newThisMonth || 0,
      };
    } catch (error) {
      console.error('Erreur API getClientsStats:', error);
      return { total: 0, newThisMonth: 0 };
    }
  },

  // Nouvelle fonction : Compter le nombre total de clients (avec recherche optionnelle)
  async getClientsCount(searchTerm: string = ''): Promise<number> {
    try {
      let query = supabase.from('clients').select('*', { count: 'exact', head: true });

      // Ajouter la recherche si un terme est fourni
      if (searchTerm.trim()) {
        query = query.or(
          `full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`
        );
      }

      const { count, error } = await query;

      if (error) {
        console.error('Erreur lors du comptage des clients:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Erreur API getClientsCount:', error);
      return 0;
    }
  },

  // Créer un nouveau client
  async createClient(
    clientData: Omit<Client, 'id' | 'created_at' | 'updated_at'>
  ): Promise<Client | null> {
    try {
      const { data, error } = await supabase.from('clients').insert(clientData).select().single();

      if (error) {
        console.error('Erreur lors de la création du client:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur API createClient:', error);
      return null;
    }
  },

  // Mettre à jour un client
  async updateClient(clientId: string, updates: Partial<Client>): Promise<Client | null> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', clientId)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la mise à jour du client:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur API updateClient:', error);
      return null;
    }
  },

  // Supprimer un client
  async deleteClient(clientId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Vérifier d'abord si le client a des commandes associées
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('id')
        .eq('client_id', clientId)
        .limit(1);

      if (ordersError && ordersError.code !== 'PGRST116') {
        console.error('Erreur lors de la vérification des commandes:', ordersError);
      }

      if (ordersData && ordersData.length > 0) {
        return {
          success: false,
          error:
            "Ce client ne peut pas être supprimé car il a des commandes associées. Veuillez d'abord supprimer ou modifier les commandes liées à ce client.",
        };
      }

      // Vérifier aussi dans orders2
      const { data: orders2Data, error: orders2Error } = await supabase
        .from('orders2')
        .select('id')
        .eq('client_id', clientId)
        .limit(1);

      if (orders2Error && orders2Error.code !== 'PGRST116') {
        console.error('Erreur lors de la vérification des commandes (orders2):', orders2Error);
      }

      if (orders2Data && orders2Data.length > 0) {
        return {
          success: false,
          error:
            "Ce client ne peut pas être supprimé car il a des commandes associées. Veuillez d'abord supprimer ou modifier les commandes liées à ce client.",
        };
      }

      // Vérifier aussi dans invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select('id')
        .eq('client_id', clientId)
        .limit(1);

      if (invoicesError && invoicesError.code !== 'PGRST116') {
        console.error('Erreur lors de la vérification des factures:', invoicesError);
      }

      if (invoicesData && invoicesData.length > 0) {
        return {
          success: false,
          error:
            "Ce client ne peut pas être supprimé car il a des factures associées. Veuillez d'abord supprimer ou modifier les factures liées à ce client.",
        };
      }

      // Si aucune commande ou facture associée, procéder à la suppression
      const { error } = await supabase.from('clients').delete().eq('id', clientId);

      if (error) {
        console.error('Erreur lors de la suppression du client:', error);

        // Gérer les erreurs de contrainte de clé étrangère
        if (error.code === '23503') {
          return {
            success: false,
            error:
              "Ce client ne peut pas être supprimé car il est référencé dans d'autres enregistrements (commandes, factures, etc.).",
          };
        }

        return {
          success: false,
          error: error.message || 'Une erreur est survenue lors de la suppression du client.',
        };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Erreur API deleteClient:', error);
      return {
        success: false,
        error:
          error?.message || 'Une erreur inattendue est survenue lors de la suppression du client.',
      };
    }
  },
};

// Service API pour les articles
export const articleService = {
  // Récupérer tous les articles (fonction existante - gardée pour compatibilité)
  async getAllArticles(): Promise<Article[]> {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('active', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('Erreur lors de la récupération des articles:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erreur API getAllArticles:', error);
      return [];
    }
  },

  // Nouvelle fonction : Récupérer les articles avec pagination côté serveur
  async getArticlesPaginated(
    page: number = 1,
    limit: number = 10,
    searchTerm: string = ''
  ): Promise<{ articles: Article[]; totalCount: number }> {
    try {
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase
        .from('articles')
        .select('*', { count: 'exact' })
        .eq('active', true)
        .order('name', { ascending: true })
        .range(from, to);

      // Ajouter la recherche si un terme est fourni
      if (searchTerm.trim()) {
        const spacedTerm = searchTerm.trim().split('').join(' ');
        query = query.or(
          `name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,code.ilike.%${searchTerm}%,reference.ilike.%${searchTerm}%,code.ilike.%${spacedTerm}%`
        );
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Erreur lors de la récupération paginée des articles:', error);
        return { articles: [], totalCount: 0 };
      }

      return {
        articles: data || [],
        totalCount: count || 0,
      };
    } catch (error) {
      console.error('Erreur API getArticlesPaginated:', error);
      return { articles: [], totalCount: 0 };
    }
  },

  // Nouvelle fonction : Compter le nombre total d'articles (avec recherche optionnelle)
  async getArticlesCount(searchTerm: string = ''): Promise<number> {
    try {
      let query = supabase
        .from('articles')
        .select('*', { count: 'exact', head: true })
        .eq('active', true);

      // Ajouter la recherche si un terme est fourni
      if (searchTerm.trim()) {
        query = query.or(
          `name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,code.ilike.%${searchTerm}%,reference.ilike.%${searchTerm}%`
        );
      }

      const { count, error } = await query;

      if (error) {
        console.error('Erreur lors du comptage des articles:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Erreur API getArticlesCount:', error);
      return 0;
    }
  },

  // Récupérer un article par son code
  async getArticleByCode(code: string): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('code', code.trim())
        .eq('active', true)
        .maybeSingle();

      if (error) {
        console.error('Erreur getArticleByCode:', error);
        return null;
      }
      return data;
    } catch (error) {
      console.error('Erreur API getArticleByCode:', error);
      return null;
    }
  },

  // Créer un nouvel article
  async createArticle(articleData: Omit<Article, 'id' | 'created_at'>): Promise<Article | null> {
    try {
      const { data, error } = await supabase.from('articles').insert(articleData).select().single();

      if (error) {
        console.error("Erreur lors de la création de l'article:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur API createArticle:', error);
      return null;
    }
  },

  // Mettre à jour un article
  async updateArticle(articleId: string, updates: Partial<Article>): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from('articles')
        .update(updates)
        .eq('id', articleId)
        .select()
        .single();

      if (error) {
        console.error("Erreur lors de la mise à jour de l'article:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur API updateArticle:', error);
      return null;
    }
  },

  // Supprimer un article (soft delete)
  async deleteArticle(articleId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('articles')
        .update({ active: false })
        .eq('id', articleId);

      if (error) {
        console.error("Erreur lors de la suppression de l'article:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erreur API deleteArticle:', error);
      return false;
    }
  },
};

// Service API pour les commandes
export const orderService = {
  // Récupérer toutes les commandes
  async getAllOrders(): Promise<Order[]> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(
          `
          *,
          clients(nom, prenom, email),
          users(nom, prenom)
        `
        )
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la récupération des commandes:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erreur API getAllOrders:', error);
      return [];
    }
  },

  // Créer une nouvelle commande
  async createOrder(
    orderData: Omit<Order, 'id' | 'created_at' | 'updated_at'>
  ): Promise<Order | null> {
    try {
      const { data, error } = await supabase.from('orders').insert(orderData).select().single();

      if (error) {
        console.error('Erreur lors de la création de la commande:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur API createOrder:', error);
      return null;
    }
  },
};
